import { showErrorNotification, showSuccessNotification } from '../../global/notifications';
import { initializeTerminationUi } from './commission-plan-builder/commission-plan-termination';
import { triggerPlanUpdated } from './event-handlers';
import { loadAreaConfigWindow, destroyAreaConfig } from './area-config-window';

let _selectedEmployee = null;
let _selectedPlan = null;
let _selectedPlanAssignmentId = null;
let _$commissionsGrid = null;
let _planId = null;
let _effectiveDate = null;
let _completionDate = null;
let _$employeeName = null;
let _$employeeNumber = null;
let _$planName = null;
let _$builderSaveButton = null;
let _$builderCommitButton = null;
let _$saveTargetSpinner = null;
let _$commissionGridControls = null;
let _commitmentDate = null;
let _terminationDate = null;
let _enableSalesTargets = null;
let _enableActualTargets = null;

let $configureAreaButton = null;
let $configureAreaWindow = null;
let _k_configureAreaWindow = null;

export function loadCommissionPlanBuilder(selectedEmployee, selectedPlan, selectedPlanAssignmentId) {
    _selectedEmployee = selectedEmployee;
    _selectedPlan = selectedPlan;
    _selectedPlanAssignmentId = selectedPlanAssignmentId;

    $('#CommissionBuilderPane').load(
        '/Commissions/CommissionPlanAssignment/GetCommissionPlanBuilderPane',
        { employeeNumber: _selectedEmployee.EmployeeNumber, planAssignmentId: _selectedPlanAssignmentId },
        () => {
            initializeElementVariables()
                .then(() => setInfoSection())
                .then(() => initializeGrid())
                .then(() => initializeSaveButton())
                .then(() => setupConfigureAreaElements())
                .then(() => initializeTerminationUi(_effectiveDate, _completionDate, _selectedEmployee, _selectedPlan))
                .finally(() => {
                    $('#CommissionBuilderPane').show();
                });
        }
    );
}

function initializeElementVariables() {
    return new Promise(resolve => {
        _$commissionsGrid = $('#commission-plan-builder-grid');
        _planId = _$commissionsGrid.data('plan-id');
        _effectiveDate = _$commissionsGrid.data('effective-date');
        _completionDate = _$commissionsGrid.data('completion-date');
        _commitmentDate = _$commissionsGrid.data('commitment-date');
        _terminationDate = _$commissionsGrid.data('termination-date');
        _enableSalesTargets = _$commissionsGrid.data('enable-sales').toUpperCase() === 'TRUE';
        _enableActualTargets = _$commissionsGrid.data('enable-actual').toUpperCase() === 'TRUE';
        _$employeeName = $('#plan-builder-employee');
        _$employeeNumber = $('#plan-builder-employee-number');
        _$planName = $('#plan-builder-plan');
        _$builderSaveButton = $('#plan-save-button');
        _$builderCommitButton = $('#plan-commit-button');
        _$saveTargetSpinner = $('#save-target-loading');
        _$commissionGridControls = $('#commission-plan-builder-controls');

        resolve();
    });
}

function setInfoSection() {
    return new Promise(resolve => {
        _$employeeName.text(_selectedEmployee.EmployeeName);
        _$employeeNumber.text(_selectedEmployee.EmployeeNumber);
        _$planName.text(_selectedPlan.Name);

        resolve();
    });
}

function isCommittedAndEffective() {
    return _commitmentDate && kendo.parseDate(_effectiveDate) < new Date();
}

function isTerminated() {
    return !!_terminationDate;
}

function shouldDisableUi() {
    return isCommittedAndEffective() || isTerminated();
}

function initializeGrid() {
    return new Promise(resolve => {
        const isMonthly = _selectedPlan.PayoutTiming === 'monthly';

        const targetField = isMonthly ? 'Month' : 'Quarter';

        const monthlyFields = {
            month: { editable: false, type: 'date' },
            planId: { editable: false, type: 'string' },
        };

        const quarterlyFields = {
            quarter: { editable: false, type: 'date' },
            planId: { editable: false, type: 'string' },
        };

        if (_enableSalesTargets) {
            monthlyFields.salesTarget = { type: 'number' };
            quarterlyFields.salesTarget = { type: 'number' };
        }

        if (_enableActualTargets) {
            monthlyFields.gpTarget = { type: 'number' };
            quarterlyFields.gpTarget = { type: 'number' };
        }

        const gridDataSource = new kendo.data.DataSource({
            transport: {
                read: {
                    url: `/Commissions/CommissionPlanAssignment/GetCommissionTargets?employeeNumber=${_selectedEmployee.EmployeeNumber}&planAssignmentId=${_selectedPlanAssignmentId}`,
                    dataType: 'json',
                },
            },
            schema: {
                model: {
                    id: 'planId',
                    fields: isMonthly ? monthlyFields : quarterlyFields
                },
            },
        });

        const templateFunction = isMonthly ?
            function (month) { return kendo.toString(kendo.parseDate(month.Month), 'MMMM yyyy'); } :
            function (quarter) { return `Q${Math.floor(kendo.parseDate(quarter.Quarter).getMonth() / 3) + 1} ${kendo.parseDate(quarter.Quarter).getFullYear()}` }

        const columns =
            [
                {
                    field: targetField,
                    title: targetField,
                    editable(dataItem) { return false; },
                    template: templateFunction,
                },
            ];

        if (_enableSalesTargets) {
            columns.push({
                field: 'SalesTarget',
                title: 'Sales GP Target',
                format: '{0:c2}',
                editor: numericTextboxEditor,
            });
        }

        if (_enableActualTargets) {
            columns.push({
                field: 'GpTarget',
                title: 'Actual GP Target',
                format: '{0:c2}',
                editor: numericTextboxEditor,
            });
        }

        _$commissionsGrid.kendoGrid({
            editable: !shouldDisableUi(),
            dataSource: gridDataSource,
            columns,
            dataBound: e => {
                _$commissionGridControls.show();
            },
        });

        resolve();
    });

    function numericTextboxEditor(container, options) {
        $(`<input name="${options.field}" />`)
            .appendTo(container)
            .kendoNumericTextBox({
                spinners: false,
                format: '{0:c2}',
                decimals: 2,
            });
    }
}

function getUpdatedEntries() {
    const changedFields = $('#commission-plan-builder-grid')
        .data('kendoGrid')
        .dataSource.data();
    const salesTargets = [];
    const gpTargets = [];

    changedFields.forEach(value => {
        if (_selectedPlan.PayoutTiming === 'monthly') {
            if (_enableSalesTargets) {
                salesTargets.push({
                    Month: value.Month,
                    DollarValue: value.SalesTarget,
                });
            }

            if (_enableActualTargets) {
                gpTargets.push({
                    Month: value.Month,
                    DollarValue: value.GpTarget,
                });
            }
        }

        if (_selectedPlan.PayoutTiming === 'quarterly') {
            if (_enableSalesTargets) {
                salesTargets.push({
                    Quarter: value.Quarter,
                    DollarValue: value.SalesTarget,
                });
            }

            if (_enableActualTargets) {
                gpTargets.push({
                    Quarter: value.Quarter,
                    DollarValue: value.GpTarget,
                });
            }
        }

    });

    return {
        salesTargets: _enableSalesTargets ? salesTargets : null,
        gpTargets: _enableActualTargets ? gpTargets : null,
    };
}

function initializeSaveButton() {
    _$builderSaveButton.click(event => {
        performSave(false);
    });

    _$builderCommitButton.click(event => {
        kendo
            .confirm(
                `Are you sure you want to commit the '${_selectedPlan.Name}' commission plan for ${_selectedEmployee.EmployeeName}
            effective ${_effectiveDate}?
            Once the plan is committed and effective you will be unable to make changes without terminating the existing plan.`
            )
            .done(() => {
                performSave(true);
            });
    });

    _$builderSaveButton.prop('disabled', shouldDisableUi());
    _$builderCommitButton.prop('disabled', shouldDisableUi());
}

function performSave(commit) {
    saveTargets(commit)
        .then(response => {
            const savedEntries = getUpdatedEntries();
            const hasZeroEntry =
                savedEntries.salesTargets.some(t => t.DollarValue === 0) || !!savedEntries.gpTargets?.some(t => t.DollarValue === 0);

            showSuccessNotification(hasZeroEntry ? "'0' value months will be ignored for Kickers" : response);

            if (commit) {
                _commitmentDate = new Date();
            }

            $('#commission-plan-builder-grid').data('kendoGrid').setOptions({ editable: !shouldDisableUi() });
        })
        .catch(error => showErrorNotification(error))
        .finally(() => {
            triggerPlanUpdated();
            _$builderSaveButton.prop('disabled', shouldDisableUi());
            _$builderCommitButton.prop('disabled', shouldDisableUi());
            _$saveTargetSpinner.hide();
        });
}

function saveTargets(commit) {
    return new Promise((resolve, reject) => {
        const { salesTargets, gpTargets } = getUpdatedEntries();

        if (commit && !validateTargetsForCommit(salesTargets, gpTargets)) {
            reject(new Error('All targets must be entered and be at least $0 to commit a plan assignment.'));
            return;
        }

        _$builderSaveButton.prop('disabled', true);
        _$builderCommitButton.prop('disabled', true);
        _$saveTargetSpinner.show();

        const data = {
            Id: _planId,
            EmployeeNumber: _selectedEmployee.EmployeeNumber,
            EmployeeName: _selectedEmployee.EmployeeName,
            PlanConfigurationId: _selectedPlan.Id,
            EffectiveDate: _effectiveDate,
            CompletionDate: _completionDate,
            MonthlyTargets: _selectedPlan.PayoutTiming !== 'monthly' ? null : { },
            QuarterlyTargets: _selectedPlan.PayoutTiming !== 'quarterly' ? null : { },
        };

        if (_selectedPlan.PayoutTiming === 'monthly') {
            if (_enableSalesTargets) {
                data.MonthlyTargets.MonthlySalesTargets = salesTargets;
            }

            if (_enableActualTargets) {
                data.MonthlyTargets.MonthlyGpTargets = gpTargets;
            }
        }
        else if (_selectedPlan.PayoutTiming === 'quarterly') {
            if (_enableSalesTargets) {
                data.QuarterlyTargets.QuarterlySalesTargets = salesTargets;
            }

            if (_enableActualTargets) {
                data.QuarterlyTargets.QuarterlyGpTargets = gpTargets;
            }
        }

        $.post(`/Commissions/CommissionPlanAssignment/SaveTargets?commit=${commit}`, data, response => {
            if (response) {
                $('#commission-plan-builder-grid')
                    .data('kendoGrid')
                    .dataSource.read();
                resolve('success');
                return;
            }

            reject(new Error('An error occurred attempting to save targets.'));
        }).fail((response) => reject(new Error(response.responseText)));
    });
}

function validateTargetsForCommit(salesTargets, gpTargets) {
    if (_enableSalesTargets && salesTargets == null) {
        return false;
    }

    if (_enableSalesTargets && !salesTargets.every(x => (x.DollarValue || x.DollarValue === 0) && x.DollarValue >= 0)) {
        return false;
    }

    if (!_enableSalesTargets && salesTargets != null) {
        return false;
    }

    if (_enableActualTargets && gpTargets == null) {
        return false;
    }

    if (_enableActualTargets && !gpTargets.every(x => (x.DollarValue || x.DollarValue === 0) && x.DollarValue >= 0)) {
        return false;
    }

    if (!_enableActualTargets && gpTargets != null) {
        return false;
    }

    return true;
}

function setupConfigureAreaElements() {
    $configureAreaWindow = $('#plan-assignment-area-config');

    if (_k_configureAreaWindow) {
        _k_configureAreaWindow.destroy();
    }

    _k_configureAreaWindow = $configureAreaWindow.kendoWindow({
        actions: ['Close'], title: 'Area Configuration', visible: false, size: 'medium', modal: true, scrollable: true, draggable: true, position: {
            top: 100, left: 100
        },
        close: (e) => destroyAreaConfig()
    }).data('kendoWindow');

    $configureAreaButton = $('#configure-area');

    $configureAreaButton.prop('disabled', shouldDisableUi());

    $configureAreaButton.on('click', function () {
        const data = {
            employeeNumber: _selectedEmployee.EmployeeNumber,
            planAssignmentId: _selectedPlanAssignmentId
        };

        return new Promise((resolve, reject) => {
            $configureAreaWindow.load('/Commissions/CommissionPlanAssignment/GetAreaConfig', data, (response, status) => {
                if (status === 'error') {
                    reject(new Error('Failed to load area config'));
                    return;
                }

                initializeAreaConfig();
                resolve();
            });
        });
    });
}

function initializeAreaConfig() {
    loadAreaConfigWindow(_k_configureAreaWindow, _selectedEmployee, _selectedPlanAssignmentId);
}
